import { get, writable } from 'svelte/store';
import { invalidatePermissionInfo } from '$lib/apis/permission-cache';

import type { MenuNode, PermissionInfo, PermissionUser } from '$lib/apis/system/types';

// 权限码与菜单树在登录/会话恢复时一次性写入，页面只读不改。
// 侧栏渲染只信 menuTree，按钮显隐只信 permissionCodes——后端已把按钮型菜单
// 从树里剔除（is_hidden=TRUE），但其权限码仍进 permissions[]。
export const permissionCodes = writable<Set<string>>(new Set());
export const menuTree = writable<MenuNode[]>([]);
export const roleCodes = writable<string[]>([]);
/**
 * 超管判据，**由后端直接给结论**，前端不重算。
 *
 * ⚠ 后端 `is_super_admin()` 判三项（`is_superuser` + 平台租户 + 角色码），而前端只看得到
 * `roles` 那一项：照 `roles.includes('super_admin')` 判，一个挂着该角色码但
 * `is_superuser=false` 的平台租户账号会看到全部按钮、点下去 403。同一个判据两份实现，
 * 迟早分叉——而分叉的表现是「按钮在那儿但用不了」，用户只会以为系统坏了。
 */
export const isSuperAdmin = writable(false);
export const currentUser = writable<PermissionUser | null>(null);

export const applyPermissionInfo = (info: PermissionInfo) => {
	permissionCodes.set(new Set(info.permissions ?? []));
	menuTree.set(info.menus ?? []);
	roleCodes.set(info.roles ?? []);
	isSuperAdmin.set(info.isSuperAdmin ?? false);
	currentUser.set(info.user ?? null);
};

export const clearPermissionInfo = () => {
	invalidatePermissionInfo();
	permissionCodes.set(new Set());
	menuTree.set([]);
	roleCodes.set([]);
	isSuperAdmin.set(false);
	currentUser.set(null);
};

export const hasPerm = (code: string): boolean =>
	grantedBy(get(permissionCodes), code, 'any', get(isSuperAdmin));

export const hasAnyPerm = (...codes: string[]): boolean =>
	grantedBy(get(permissionCodes), codes, 'any', get(isSuperAdmin));

export const hasAllPerm = (...codes: string[]): boolean =>
	grantedBy(get(permissionCodes), codes, 'all', get(isSuperAdmin));

// 组件层用的纯函数版本：传入已订阅的 Set，避免在响应式语句里调用 get() 而错过更新。
// `superAdmin` 同理必须由调用方传入已订阅的值，不在这里 get()。
//
// ⚠ 首行的超管旁路对齐后端 `UserInfo.has_permission`——那一侧首行就是
// `if self.is_super_admin(): return True`。两侧口径不一致的失败形态是**默认形态**而不是
// 个别菜单漏种：超管角色的授权漏掉哪一条，按钮就在超管界面上消失，而接口对他完全开放。
// 「按钮不显示但接口开放」比反过来更难查，因为没有任何报错。
//
// 访问他租户时这条旁路自动失效，不必另做判断：后端 `_apply_visit_tenant_permissions`
// 会把 `role_codes` 覆写成目标租户的 `tenant_admin`，而前端读的就是下发的这一份。
export const grantedBy = (
	codes: Set<string>,
	perm: string | string[],
	mode: 'any' | 'all' = 'any',
	superAdmin = false
): boolean => {
	if (superAdmin) return true;
	const list = Array.isArray(perm) ? perm : [perm];
	if (list.length === 0) return true;
	return mode === 'all' ? list.every((c) => codes.has(c)) : list.some((c) => codes.has(c));
};
